import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import ModalPortal from "../common/ModalPortal";

/**
 * Multi-select picker used by all three "link entries" sections of the Add /
 * Update Diagnosis dialogs (medicines, chief complaints, labs).
 *
 * Flat chip grid — selected chips highlight (purple + check); unselected show
 * a `+`. Simple client-side search box narrows the list. Footer shows the
 * current selection count and Cancel / Add-selected buttons; Add closes the
 * modal after committing the selection back to the parent via `onCommit`.
 *
 * Deliberately parameterised — same component powers all three sources by
 * varying `title`, `subtitle`, `items`, `getKey`, `getLabel`, `getSubLabel`.
 */
const RefPickerModal = ({
  title,
  subtitle,
  items,
  initialSelectedIds,
  getKey,
  getLabel,
  getSubLabel,
  getStatusMark,
  onCommit,
  onClose,
}) => {
  const [selected, setSelected] = useState(() => new Set(initialSelectedIds || []));
  const [query, setQuery] = useState("");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items || [];
    return (items || []).filter((it) => {
      const label = (getLabel(it) || "").toLowerCase();
      const sub = getSubLabel ? (getSubLabel(it) || "").toLowerCase() : "";
      return label.includes(q) || sub.includes(q);
    });
  }, [items, query, getLabel, getSubLabel]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const commit = () => {
    onCommit(Array.from(selected));
    onClose();
  };

  return (
    <ModalPortal>
      <div className="cc-modal-backdrop" onMouseDown={onClose}>
        <div
          className="cc-modal dx-refpicker"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <header className="cc-modal-head">
            <div>
              <span className="cc-modal-title">{title}</span>
              {subtitle && <span className="cc-modal-sub"> · {subtitle}</span>}
            </div>
            <button
              type="button"
              className="cc-modal-close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </header>
  
          <div className="cc-modal-body">
            <div className="sec-search dx-refpicker-search">
              <svg
                className="sec-search-icon"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                aria-hidden="true"
              >
                <circle cx="9" cy="9" r="6" />
                <path d="M14 14l4 4" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
              />
            </div>
  
            {filtered.length === 0 ? (
              <p className="dx-refpicker-empty">No matches.</p>
            ) : (
              <div className="dx-refpicker-grid">
                {filtered.map((it) => {
                  const id = getKey(it);
                  const on = selected.has(id);
                  const mark = getStatusMark ? getStatusMark(it) : null;
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`dx-strip-chip is-in-picker${on ? " is-on" : ""}`}
                      onClick={() => toggle(id)}
                      aria-pressed={on}
                    >
                      {on ? (
                        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                          <path d="M4 10.5l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                          <path d="M10 4v12M4 10h12" strokeLinecap="round" />
                        </svg>
                      )}
                      <span className="dx-strip-chip-label">{getLabel(it)}</span>
                      {mark && <span className="dx-strip-chip-mark">{mark}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
  
          <footer className="cc-modal-foot">
            <button
              type="button"
              className="sec-btn sec-btn-ghost"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="sec-btn sec-btn-primary"
              onClick={commit}
            >
              Add selected ({selected.size})
            </button>
          </footer>
        </div>
      </div>
    </ModalPortal>
  );
};

RefPickerModal.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  items: PropTypes.array,
  initialSelectedIds: PropTypes.array,
  getKey: PropTypes.func.isRequired,
  getLabel: PropTypes.func.isRequired,
  getSubLabel: PropTypes.func,
  getStatusMark: PropTypes.func,
  onCommit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default RefPickerModal;
